import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import type { BlotterFeedType } from "../../services/blotterApi";
import { ORDER_EVENT } from "@/services/consts";

/**
 * Central state management hook for the Blotter page.
 *
 * Manages:
 * - Feed type selection (synced with URL param 'feed_type')
 * - Loading states
 *
 * URL Synchronization:
 * - Reads feed_type from URL on mount
 * - Updates URL when feed type changes
 */
export const useBlotterState = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial feed type from URL or default to ORDER_EVENT
  const urlFeedType = searchParams.get("feed_type") as BlotterFeedType | null;
  const initialFeedType = urlFeedType || ORDER_EVENT;

  // State
  const [feedType, setFeedTypeState] = useState<BlotterFeedType>(initialFeedType);
  const [loading, setLoading] = useState<boolean>(false);

  // Update URL when feed type changes
  useEffect(() => {
    const currentFeedType = searchParams.get("feed_type");
    if (currentFeedType !== feedType) {
      setSearchParams((prev) => {
        prev.set("feed_type", feedType);
        return prev;
      }, { replace: true });
    }
  }, [feedType, searchParams, setSearchParams]);

  // Memoized setter that updates state
  const setFeedType = useCallback((newFeedType: BlotterFeedType) => {
    setFeedTypeState(newFeedType);
  }, []);

  return {
    feedType,
    setFeedType,
    loading,
    setLoading,
  };
};
